/**
 * Subscription Plans Data
 * Defines all available subscription plans with pricing and features
 * Supports both monthly and yearly billing with savings calculations
 */

export const SUBSCRIPTION_PLANS = {
  basic: {
    id: 'basic',
    name: 'Basic',
    description: 'Perfect for small teams getting started',
    monthlyPrice: 999,
    yearlyPrice: 9999,
    currency: 'INR',
    interval: 'month',
    yearlyInterval: 'year',
    yearlySavings: 1989, // 2 months saving
    features: [
      'Order Management System',
      'Real-time Order Updates',
      'Mobile App Access'
    ],
    limits: {
      maxUsers: 6,
      maxOrders: 80,
      backupDays: 30
    },
    popular: false,
    recommended: false
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    description: 'Ideal for growing businesses',
    monthlyPrice: 1499,
    yearlyPrice: 14999,
    currency: 'INR',
    interval: 'month',
    yearlyInterval: 'year',
    yearlySavings: 2989, // 2 months saving
    features: [
      'Everything in Basic',
      'Priority Customer Support',
      'Advanced Analytics & Reports'
    ],
    limits: {
      maxUsers: 12,
      maxOrders: 200,
      backupDays: 65
    },
    popular: true,
    recommended: true
  },
  premiumPlus: {
    id: 'premiumPlus',
    name: 'Premium+',
    description: 'For established businesses with high volume',
    monthlyPrice: 2199,
    yearlyPrice: 21999,
    currency: 'INR',
    interval: 'month',
    yearlyInterval: 'year',
    yearlySavings: 4389, // 2 months saving
    features: [
      'Everything in Premium',
      'Advanced User Management',
      'Dedicated Account Manager'
    ],
    limits: {
      maxUsers: 20,
      maxOrders: 400,
      backupDays: 90
    },
    popular: false,
    recommended: false
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Unlimited scale for large organizations',
    monthlyPrice: 3199,
    yearlyPrice: 35199,
    currency: 'INR',
    interval: 'month',
    yearlyInterval: 'year',
    yearlySavings: 3189, // 2 months saving
    features: [
      'Everything in Premium Plus',
      'Unlimited Users',
      'Unlimited Orders',
      'Forever Data Backup',
      '24/7 Priority Support'
    ],
    limits: {
      maxUsers: -1, // Unlimited
      maxOrders: -1, // Unlimited
      backupDays: -1, // Forever
    },
    popular: false,
    recommended: false
  }
};

// Additional user pricing
export const ADDITIONAL_USER_PRICE = 50; // One-time charge per additional user

// Plan comparison helper
export const getPlanComparison = () => {
  return Object.values(SUBSCRIPTION_PLANS).map(plan => ({
    ...plan,
    monthlyPriceFormatted: `₹${plan.monthlyPrice}`,
    yearlyPriceFormatted: `₹${plan.yearlyPrice}`,
    monthlySavingsFormatted: `Save ₹${plan.yearlySavings}`,
    yearlySavingsFormatted: `Save ₹${plan.yearlySavings}`,
    usersDisplay: plan.limits.maxUsers === -1 ? 'Unlimited' : `${plan.limits.maxUsers} users`,
    ordersDisplay: plan.limits.maxOrders === -1 ? 'Unlimited' : `${plan.limits.maxOrders} orders`,
    backupDisplay: plan.limits.backupDays === -1 ? 'Forever' : `${plan.limits.backupDays} days`
  }));
};

// Get plan by ID
export const getPlanById = (planId) => {
  return SUBSCRIPTION_PLANS[planId] || null;
};

// Calculate additional user cost
export const calculateAdditionalUserCost = (currentUsers, planMaxUsers) => {
  if (planMaxUsers === -1) return 0; // Unlimited users
  const additionalUsers = Math.max(0, currentUsers - planMaxUsers);
  return additionalUsers * ADDITIONAL_USER_PRICE;
};

// Get recommended plan based on user count
export const getRecommendedPlan = (userCount) => {
  if (userCount <= 6) return 'basic';
  if (userCount <= 12) return 'premium';
  if (userCount <= 20) return 'premiumPlus';
  return 'enterprise';
};

// Format price for display
export const formatPrice = (price, currency = 'INR') => {
  if (price === undefined || price === null || isNaN(price)) {
    return '₹0';
  }
  return `₹${price.toLocaleString('en-IN')}`;
};

// Calculate yearly savings percentage
export const calculateYearlySavingsPercentage = (monthlyPrice, yearlyPrice) => {
  const monthlyTotal = monthlyPrice * 12;
  const savings = monthlyTotal - yearlyPrice;
  return Math.round((savings / monthlyTotal) * 100);
};